import {FC, MouseEvent, useState} from 'react';
import {CreditCardOutlined} from '@mui/icons-material';
import {Box, Popover} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetIcons} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {rfidItemCardSx} from './RfidItemCard.sx';
import {RfIdItemCardType} from './RfIdItemCard.types';
import {TagsDTO} from 'src/types/tags';
import {RfidActions} from '../RfidActions';
import {rfidTableSx} from '../RfidTable.sx';
import {BlockStatusEnum} from '../RfidTable.types';
import dayjs from 'dayjs';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {MenuButton} from '../../../tables/respnsiveTable/MenuButton';

export const RfIdItemCard: FC<RfIdItemCardType> = ({data, businessId}) => {
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    // Company tags carry richer associations (TagsDTO) than the base OCPPTagDTO type
    const tagDetails = data as unknown as Partial<TagsDTO>;

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };


    // One node, drawn by whichever of the two the width calls for: every handler and every
    // permission gate inside it is the same in both.
    const tagActions = (
        <RfidActions
            businessId={businessId}
            blocked={data.blocked}
            ocppTagId={data.id}
            idTag={data.idTag}
            licensePlate={data.licensePlate}
            tagCompanyId={tagDetails.company?.id}
            tagEmployeeId={tagDetails.employee?.id}
            tagUserId={tagDetails.users?.[0]?.id}
        />
    );

    // The card's one menu, in the head rather than absolutely placed over the corner of the card.
    const menu = (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<CreditCardOutlined/>}
                    title={data?.idTag}
                    subtitle={data?.licensePlate || undefined}
                >
                    <ActionSheetIcons>{tagActions}</ActionSheetIcons>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={rfidItemCardSx.popover}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {tagActions}
                </Popover>
            )}
        </>
    );

    /**
     * The way into the tag's own page.
     *
     * Same destination as the tags list's own card and the same one written in the column beside it,
     * so a tag opens the same way wherever it is listed.
     */
    const tagTitle = (
        <Box
            component={Link}
            to={`/app/rfids/${data?.id}`}
            sx={{color: 'inherit', textDecoration: 'none'}}
        >
            {data?.idTag ?? ''}
        </Box>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<CreditCardOutlined/>}
                title={tagTitle}
                action={menu}
            />
            <Box sx={phonePillsRowSx}>
                <Box
                    sx={[
                        rfidTableSx.tag,
                        !data.blocked && rfidTableSx.tagActive,
                        data.blocked && rfidTableSx.tagBlocked,
                    ]}
                >
                    <FormattedMessage
                        id={data.blocked ? BlockStatusEnum.blocked : BlockStatusEnum.accepted}
                    />
                </Box>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: <FormattedMessage id="licensePlate"/>,
                        value: data?.licensePlate || '—',
                    },
                    {
                        label: <FormattedMessage id="creationDate"/>,
                        value: data.created ? dayjs(data.created).format('DD/MM/YYYY') : '—',
                    },
                ]}
            />
        </TableCard>
    );
};
